import React, { useState } from 'react';
import { Field, FieldAttributes, Form as FormikForm, Formik } from 'formik';
import { useMutation } from 'react-query';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import ApiProvider from '../../../../../core/api/ApiProvider';
import { InfoMessageService, TranslationService } from '../../../../../core/services';
import { useAuthenticationContext } from '../../../../../core/services/AuthenticationProvider';
import { DictionaryType } from '../../../../../core/types/DictionaryType';
import { ResponseApiType } from '../../../../types/ResponseApiType';

type PropsType = {
  changePassVM: changePassVMType;
};

type changePassVMType = {
  forcedToChangePassword: boolean;
  validationInfo: string;
  validationRules: ValidationRulesType;
};

type ValidationRulesType = {
  atLeastOneCapitalLetter: boolean;
  atLeastOneNumber: boolean;
  atLeastOneSmallLetter: boolean;
  atLeastOneSpecialChar: boolean;
  maxLength: number;
  minLength: number;
};

type PasswordFieldName = 'userOldPassword' | 'userNewPassword' | 'newPasswordAgain';

const initialValues = {
  userOldPassword: '',
  userNewPassword: '',
  newPasswordAgain: '',
};

const ChangePassword = (props: PropsType) => {
  const authContext = useAuthenticationContext();
  const [visibleFields, setVisibleFields] = useState<Record<PasswordFieldName, boolean>>({
    userOldPassword: false,
    userNewPassword: false,
    newPasswordAgain: false,
  });

  const toggleVisibility = (field: PasswordFieldName) =>
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));

  const changePassword = useMutation(
    async (variables: DictionaryType<any>) => {
      return await ApiProvider.post<ResponseApiType>(
        '/UserProfileModule/ChangePassword',
        variables.values
      );
    },
    {
      onSuccess: (data, variables) => {
        if (data.success) {
          variables.resetForm({ values: initialValues });

          InfoMessageService.success(TranslationService.translate('GeneralSuccessMsg'));
        } else {
          throw data.message;
        }

        authContext.signinSilent && authContext.signinSilent();
      },
      onError: () => {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      },
    }
  );

  const rules = props.changePassVM.validationRules;
  const renderPasswordRules = (newPassword: string) => {
    const items: { met: boolean; label: string }[] = [];

    if (rules.minLength || rules.maxLength) {
      const withinRange =
        newPassword.length >= rules.minLength && newPassword.length <= rules.maxLength;
      items.push({
        met: withinRange,
        label: `${TranslationService.translate('FieldMinLength')} ${rules.minLength} / ${TranslationService.translate('FieldMaxLength')} ${rules.maxLength}`,
      });
    }
    if (rules.atLeastOneCapitalLetter) {
      items.push({
        met: /[A-Z]/.test(newPassword),
        label: TranslationService.translate('ValidRulesAtLeastOneCapitalLetter'),
      });
    }
    if (rules.atLeastOneSmallLetter) {
      items.push({
        met: /[a-z]/.test(newPassword),
        label: TranslationService.translate('ValidRulesAtLeastOneSmallLetter'),
      });
    }
    if (rules.atLeastOneNumber) {
      items.push({
        met: /[0-9]/.test(newPassword),
        label: TranslationService.translate('ValidRulesAtLeastOneNumber'),
      });
    }
    if (rules.atLeastOneSpecialChar) {
      items.push({
        met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(newPassword),
        label: TranslationService.translate('ValidRulesAtLeastOneSpecialChar'),
      });
    }

    return (
      <List dense disablePadding>
        {items.map((item, i) => (
          <ListItem key={i} disableGutters sx={{ py: 0 }}>
            <ListItemIcon sx={{ minWidth: 28, justifyContent: 'center' }}>
              {item.met ? (
                <CheckCircleOutlinedIcon fontSize="small" color="success" />
              ) : (
                <FiberManualRecordIcon sx={{ fontSize: 8 }} color="disabled" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{
                primary: {
                  variant: 'body2',
                  color: item.met ? 'success.main' : 'text.secondary',
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardHeader
        avatar={<LockResetIcon color="primary" />}
        title={
          <Typography variant="h6" component="h2">
            {`${TranslationService.translate('Change')} ${TranslationService.translate('Password')}`}
          </Typography>
        }
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Stack spacing={2}>
          {props.changePassVM.forcedToChangePassword && (
            <Alert severity="warning" variant="outlined">
              {TranslationService.translate('PasswordChangeRecommended')}
            </Alert>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema(props.changePassVM.validationRules)}
            onSubmit={(values, { resetForm }) => {
              changePassword.mutate({ values, resetForm });
            }}
            enableReinitialize={true}
          >
            {({ values }) => (
              <FormikForm noValidate>
                <Stack spacing={2}>
                  <Field name="userOldPassword">
                    {(fieldProps: FieldAttributes<any>) => (
                      <TextField
                        variant="outlined"
                        id="userOldPassword"
                        label={TranslationService.translate('OldPassword')}
                        type={visibleFields.userOldPassword ? 'text' : 'password'}
                        value={fieldProps.field.value ?? ''}
                        onChange={fieldProps.form.handleChange}
                        onBlur={() => fieldProps.form.setFieldTouched('userOldPassword', true)}
                        name={fieldProps.field.name}
                        autoComplete="current-password"
                        required
                        error={!!(fieldProps.meta.touched && fieldProps.meta.error)}
                        helperText={
                          fieldProps.meta.touched && fieldProps.meta.error
                            ? String(fieldProps.meta.error)
                            : undefined
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => toggleVisibility('userOldPassword')}
                                  edge="end"
                                  size="small"
                                >
                                  {visibleFields.userOldPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  </Field>

                  <Field name="userNewPassword">
                    {(fieldProps: FieldAttributes<any>) => (
                      <TextField
                        variant="outlined"
                        id="userNewPassword"
                        label={TranslationService.translate('NewPassword')}
                        type={visibleFields.userNewPassword ? 'text' : 'password'}
                        value={fieldProps.field.value ?? ''}
                        onChange={fieldProps.form.handleChange}
                        onBlur={() => fieldProps.form.setFieldTouched('userNewPassword', true)}
                        name={fieldProps.field.name}
                        autoComplete="new-password"
                        required
                        error={!!(fieldProps.meta.touched && fieldProps.meta.error)}
                        helperText={
                          fieldProps.meta.touched && fieldProps.meta.error
                            ? String(fieldProps.meta.error)
                            : undefined
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => toggleVisibility('userNewPassword')}
                                  edge="end"
                                  size="small"
                                >
                                  {visibleFields.userNewPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  </Field>

                  <Box>{renderPasswordRules(values.userNewPassword)}</Box>

                  <Field name="newPasswordAgain">
                    {(fieldProps: FieldAttributes<any>) => (
                      <TextField
                        variant="outlined"
                        id="newPasswordAgain"
                        label={TranslationService.translate('RepeatPassword')}
                        type={visibleFields.newPasswordAgain ? 'text' : 'password'}
                        value={fieldProps.field.value ?? ''}
                        onChange={fieldProps.form.handleChange}
                        onBlur={() => fieldProps.form.setFieldTouched('newPasswordAgain', true)}
                        name={fieldProps.field.name}
                        autoComplete="new-password"
                        required
                        error={!!(fieldProps.meta.touched && fieldProps.meta.error)}
                        helperText={
                          fieldProps.meta.touched && fieldProps.meta.error
                            ? String(fieldProps.meta.error)
                            : undefined
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => toggleVisibility('newPasswordAgain')}
                                  edge="end"
                                  size="small"
                                >
                                  {visibleFields.newPasswordAgain ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  </Field>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={changePassword.isLoading}
                      startIcon={
                        changePassword.isLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                    >
                      {TranslationService.translate('Save')}
                    </Button>
                  </Box>
                </Stack>
              </FormikForm>
            )}
          </Formik>
        </Stack>
      </CardContent>
    </Card>
  );
};

const validationSchema = (validation: ValidationRulesType) => {
  return Yup.object().shape({
    userOldPassword: Yup.string()
      .typeError(TranslationService.translate('RequiredField'))
      .required(TranslationService.translate('RequiredField')),
    userNewPassword: Yup.string()
      .typeError(TranslationService.translate('RequiredField'))
      .min(
        validation.minLength,
        `${TranslationService.translate('FieldMinLength')} ${validation.minLength}`
      )
      .max(
        validation.maxLength,
        `${TranslationService.translate('FieldMaxLength')} ${validation.maxLength}`
      )
      .test(
        'atLeastOneCapitalLetter',
        TranslationService.translate('ValidRulesAtLeastOneCapitalLetter'),
        (value) => {
          if (validation.atLeastOneCapitalLetter && value) {
            if (!/[A-Z]/.test(value)) {
              return false;
            }
          }
          return true;
        }
      )
      .test(
        'atLeastOneNumber',
        TranslationService.translate('ValidRulesAtLeastOneNumber'),
        (value) => {
          if (validation.atLeastOneNumber && value) {
            if (!/[0-9]/.test(value)) {
              return false;
            }
          }
          return true;
        }
      )
      .test(
        'atLeastOneSmallLetter',
        TranslationService.translate('ValidRulesAtLeastOneSmallLetter'),
        (value) => {
          if (validation.atLeastOneSmallLetter && value) {
            if (!/[a-z]/.test(value)) {
              return false;
            }
          }
          return true;
        }
      )
      .test(
        'atLeastOneSpecialChar',
        TranslationService.translate('ValidRulesAtLeastOneSpecialChar'),
        (value) => {
          if (validation.atLeastOneSpecialChar && value) {
            if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value)) {
              return false;
            }
          }
          return true;
        }
      )
      .required(TranslationService.translate('RequiredField')),
    newPasswordAgain: Yup.string()
      .typeError(TranslationService.translate('RequiredField'))
      .oneOf([Yup.ref('userNewPassword'), null], TranslationService.translate('PasswordMatchError'))
      .required(TranslationService.translate('RequiredField')),
  });
};

export default ChangePassword;
